import { AssetAmount, AssetRatio, IAssetAmountMetadata } from "@sundaeswap/asset";
import { UTxO } from "lucid-cardano";
import { useCallback, useMemo } from "react";
import { TasteTest, TokenStats, Transaction } from "../../gql/generated/taste-test.sdk";
import { usePoolByLPAssetId } from "../../gql/hooks/pools.query";
import { getLpTokenFromTasteTest } from "../../pages/TasteTestDetail/utils/getLpTokenFromReservedUtxo";
import { IAssetMetaData } from "../../types/Asset.types";
import { calculateAssetBalanceForLP } from "../../utils/positions.utils";
import { calculateTargetPrice as calculateTargetPriceFn } from "../../utils/taste-test.utils";

interface IUseCalculateContributionOutcomeArgs {
  latestHistoryRecord?: Transaction;
  currentBalance?: TokenStats;
  reservedUtxos?: UTxO[];
  tasteTest?: TasteTest;
}

export const useCalculateContributionOutcome = ({
  latestHistoryRecord,
  currentBalance,
  reservedUtxos,
  tasteTest,
}: IUseCalculateContributionOutcomeArgs) => {
  const lpAssetAmount = getLpTokenFromTasteTest(tasteTest, reservedUtxos);
  const { data, isLoading: isLoadingPoolByLPAssetId } = usePoolByLPAssetId(
    lpAssetAmount.metadata.assetId
  );

  const depositAmount = useMemo(
    () =>
      new AssetAmount(
        latestHistoryRecord?.depositCumulative[0].amount,
        latestHistoryRecord?.depositCumulative[0].asset as unknown as IAssetMetaData
      ),
    [latestHistoryRecord?.depositCumulative]
  );
  const gatheredAssetAmount = useMemo(
    () =>
      new AssetAmount(currentBalance?.tokenGathered.amount, currentBalance?.tokenGathered.asset),
    [currentBalance?.tokenGathered.amount, currentBalance?.tokenGathered.asset]
  );
  const suppliedAssetAmount = useMemo(
    () =>
      new AssetAmount(currentBalance?.tokenSupplied.amount, currentBalance?.tokenSupplied.asset),
    [currentBalance?.tokenSupplied.amount, currentBalance?.tokenSupplied.asset]
  );

  const calculateTargetPrice = useCallback(
    () => calculateTargetPriceFn(currentBalance),
    [currentBalance]
  );

  const calculateShareOfPool = useCallback(
    (currentDepositAmount: AssetAmount<IAssetAmountMetadata> = depositAmount) => {
      return currentDepositAmount.value.divide(gatheredAssetAmount.value.multiply(2)).toNumber();
    },
    [depositAmount, gatheredAssetAmount]
  );

  /**
   * This function calculates the outcome of the user's contribution to the pool.
   * The outcome is calculated based on the deposit amount the user made and the gathered asset amount.
   */
  const calculateContributionOutcome = useCallback(
    (currentDepositAmount: AssetAmount<IAssetAmountMetadata> = depositAmount) => {
      /* -------------------------------------------------------------------------------------------------
       * Flag to check if the there haven't been any contributions to the pool yet.
       * This is used to determine the percentage of the user's contribution to the pool.
       * -----------------------------------------------------------------------------------------------*/
      const tasteTestHasContributions = !!gatheredAssetAmount.amount;

      /* -------------------------------------------------------------------------------------------------
       * Calculate the percentages for the user's contribution to the pool.
       * If there haven't been any contributions, the user's contribution is 100% of the pool, otherwise
       * the user's contribution is being calculated based on the deposit amount and the gathered asset amount.
       * -----------------------------------------------------------------------------------------------*/
      const poolShare = tasteTestHasContributions ? calculateShareOfPool(currentDepositAmount) : 1;

      /* -------------------------------------------------------------------------------------------------
       * Based on the pool share, we are calculating what the user's contribution is worth in:
       * 1. Gathered Asset
       * 2. Supplied Asset
       * If there haven't been any contributions to the pool yet, the user's contribution is worth the
       * deposit amount they made. Otherwise, the user's contribution is calculated based on the pool share
       * and the respective asset amounts.
       * -----------------------------------------------------------------------------------------------*/
      const contributionValueAda = tasteTestHasContributions
        ? calculateAssetBalanceForLP(poolShare, gatheredAssetAmount.value.toNumber())
        : currentDepositAmount.value.toNumber() * poolShare;

      const contributionValueToken = calculateAssetBalanceForLP(
        poolShare,
        suppliedAssetAmount.value.toNumber()
      );

      /* -------------------------------------------------------------------------------------------------
       * For convenience, we convert the calculated contribution values to AssetAmounts.
       * -----------------------------------------------------------------------------------------------*/
      const contributionValueAdaAssetAmount = AssetAmount.fromValue(
        contributionValueAda,
        gatheredAssetAmount.metadata
      );

      const contributionValueTokenAssetAmount = AssetAmount.fromValue(
        contributionValueToken,
        suppliedAssetAmount.metadata
      );

      const assetRatio = new AssetRatio(
        gatheredAssetAmount.add(currentDepositAmount),
        suppliedAssetAmount
      );

      return { assetRatio, contributionValueAdaAssetAmount, contributionValueTokenAssetAmount };
    },
    [calculateShareOfPool, depositAmount, gatheredAssetAmount, suppliedAssetAmount]
  );

  /**
   * This function calculcates the outcome of the user's contribution to the pool once the taste test concluded.
   * Since the amounts can vary based on trading activity of the pool, we need to calculate the outcome based on that
   * information and not based on the deposit the user made.
   */
  const calculateClaimableAmount = useCallback(() => {
    const poolQuantityA = data?.quantityA;
    const poolQuantityB = data?.quantityB;
    const poolQuantityLP = data?.quantityLP;
    const shareA = lpAssetAmount.amount * BigInt(poolQuantityA?.quantity ?? 1);
    const shareB = lpAssetAmount.amount * BigInt(poolQuantityB?.quantity ?? 1);
    const gatheredAssetAmount = new AssetAmount(
      shareA / BigInt(poolQuantityLP?.quantity ?? 1),
      poolQuantityA?.asset as unknown as IAssetMetaData
    );
    const suppliedAssetAmount = new AssetAmount(
      shareB / BigInt(poolQuantityLP?.quantity ?? 1),
      poolQuantityB?.asset as unknown as IAssetMetaData
    );
    return { gatheredAssetAmount, suppliedAssetAmount };
  }, [data, lpAssetAmount]);

  const getCurrentBalanceAssetAmounts = useCallback(() => {
    return {
      gatheredAssetAmount,
      suppliedAssetAmount,
    };
  }, [gatheredAssetAmount, suppliedAssetAmount]);

  return {
    calculateClaimableAmount,
    calculateContributionOutcome,
    calculateShareOfPool,
    calculateTargetPrice,
    getCurrentBalanceAssetAmounts,
    isLoadingPoolByLPAssetId,
  };
};
